import React, { useContext, useMemo, useState } from 'react';
import { origins } from 'js/constants';
import { getJwtPayload } from 'js/utils';
import sectionTypes from 'js/enums/section-types.enum';
import serviceTypes from 'js/enums/service-types.enum';
import Alert from 'js/components/alert/alert';
import Box from 'js/components/box/box';
import Button from 'js/components/button/button';
import Col from 'js/components/grid/column';
import Row from 'js/components/grid/row';
import Section from 'js/components/section/section';
import SectionTitle from 'js/components/section-title/section-title';
import Text from 'js/components/text/text';
import DimensionPanelHeader from '../dimensions-panel/dimensions-panel-header';
import AIContextBuilderDrawer from '../ai-context-builder-drawer/ai-context-builder-drawer';
import PageAnalyzerDrawer from '../page-analyzer-drawer/page-analyzer-drawer';
import RulesSection from './rules-section/rules-section';
import ContextDetailContext from '../../contexts/context-detail.context';
import MatchingStrategySection from './matching-strategy-section/matching-strategy-section';

const AnalyzerSection = ({ onOpenAIContextBuilder, onOpenPageAnalyzer }) => {
    const { services } = getJwtPayload(localStorage.getItem('AuthToken'));
    const hasAIContextBuilder = !!services.find(
        (service) => service.name === serviceTypes.AI_CONTEXT_BUILDER,
    );

    const infoServices = [];
    const buttons = [];
    if (onOpenAIContextBuilder && hasAIContextBuilder) {
        buttons.push(
            <Button theme="outline" onClick={onOpenAIContextBuilder}>
                AI Context Builder
            </Button>,
        );
        infoServices.push('with the help of generative AI');
    }
    if (onOpenPageAnalyzer) {
        buttons.push(
            <Button theme="outline" onClick={onOpenPageAnalyzer}>
                Page Analyzer
            </Button>,
        );
        infoServices.push('by providing an example page');
    }

    const info = `Find out which keywords and topics you could use ${infoServices.join(
        ' or ',
    )}.`;

    if (buttons.length === 0) return null;

    return (
        <Section theme="secondary" hasBorderTop hasBorderBottom>
            <Row gutter="none">
                <Col span={12}>
                    <Box padding="base">
                        <SectionTitle info={info} extension={buttons}>
                            <Text
                                weight="bold"
                                color={['gray', 'darkest']}
                                size="large"
                            >
                                Build Out Your Context Faster
                            </Text>
                        </SectionTitle>
                    </Box>
                </Col>
            </Row>
        </Section>
    );
};

function KeywordsPanelBody({ focus }) {
    const {
        context: {
            current: { is_app: isApp },
        },
        page: { isReadOnly },
    } = useContext(ContextDetailContext);

    const [csvError, setCsvError] = useState('');
    const [isAIContextBuilderVisible, setAIContextBuilderVisible] =
        useState(false);
    const [isPageAnalyzerVisible, setPageAnalyzerVisible] = useState(false);

    const toggleAIContextBuilderVisible = () => {
        setAIContextBuilderVisible((state) => !state);
    };
    const togglePageAnalyzerVisible = () => {
        setPageAnalyzerVisible((state) => !state);
    };

    return (
        <>
            {!isReadOnly && (
                <AnalyzerSection
                    onOpenAIContextBuilder={toggleAIContextBuilderVisible}
                    onOpenPageAnalyzer={
                        isApp ? null : togglePageAnalyzerVisible
                    }
                />
            )}

            <MatchingStrategySection />

            {csvError && (
                <Box padding={['base', 'base', 0]}>
                    <Alert
                        title={csvError}
                        theme="danger"
                        actionItems={
                            <Button onClick={() => setCsvError('')}>
                                Dismiss
                            </Button>
                        }
                    >
                        <p>
                            No keywords have been added because your CSV could
                            not be processed. Please check the formatting of
                            your file, ensuring that keywords are separated by a
                            new line or comma, before trying again.
                        </p>
                    </Alert>
                </Box>
            )}

            {isAIContextBuilderVisible && (
                <AIContextBuilderDrawer
                    onClose={toggleAIContextBuilderVisible}
                />
            )}
            {isPageAnalyzerVisible && (
                <PageAnalyzerDrawer onClose={togglePageAnalyzerVisible} />
            )}

            <RulesSection focus={focus} onCsvError={setCsvError} />
        </>
    );
}

export default function KeywordsPanel({ isVisible = true }) {
    const {
        priceStructure: { display_targeting_cpm_usd: targetPrice },
    } = useContext(ContextDetailContext);

    const keywordsOrigin = useMemo(
        () => ({
            ...origins.find(({ id }) => id === sectionTypes.KEYWORDS_DIMENSION),
            cpm_usd: targetPrice,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [targetPrice],
    );

    return (
        <>
            <DimensionPanelHeader origin={keywordsOrigin} />

            <KeywordsPanelBody focus={isVisible} />
        </>
    );
}
